import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  Squares2X2Icon,
  UserCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { ChartBarIcon } from '@heroicons/react/24/solid';

export default function CommandPalette({ open, onClose, permissions, allClients = [], clientView = null }) {
  const [query, setQuery] = useState('');
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();

  // Mount → paint → fade in; fade out → unmount
  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
    }
  }, [open]);

  // Focus + reset when fully visible
  useEffect(() => {
    if (visible) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [visible]);

  const hasRouteAccess = (route) => {
    if (!permissions) return false;
    if (!permissions.routes || permissions.routes.length === 0) return true;
    return permissions.routes.includes(route);
  };

  const hasCMSAccess = () => permissions?.CMS_access === true;

  const pages = clientView ? [
    { label: 'Home', to: `/portal/${clientView.id}`, icon: Squares2X2Icon, show: true },
    { label: 'Insights', to: `/insights?view=company&id=${clientView.id}`, icon: ChartBarIcon, show: true },
  ] : [
    { label: 'Dashboard', to: '/dashboard', icon: Squares2X2Icon, show: hasRouteAccess('/dashboard') },
    { label: 'Insights', to: '/insights', icon: ChartBarIcon, show: hasRouteAccess('/insights') },
    { label: 'Clients', to: '/clients', icon: UserCircleIcon, show: hasRouteAccess('/clients') },
    { label: 'CMS', to: '/cms', icon: PencilSquareIcon, show: hasCMSAccess() },
  ].filter((p) => p.show);

  const q = query.toLowerCase();
  const filteredPages = q ? pages.filter((p) => p.label.toLowerCase().includes(q)) : pages;
  const filteredClients = clientView ? [] : allClients.filter(
    (c) =>
      !q ||
      c.name.toLowerCase().includes(q) ||
      (c.industry && c.industry.toLowerCase().includes(q)),
  );

  const totalResults = filteredPages.length + filteredClients.length;
  const clientOffset = filteredPages.length;

  // Reset selection to 0 when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const go = useCallback((to) => {
    navigate(to);
    onClose();
  }, [navigate, onClose]); // Dependencies of the 'go' function itself

  // Keep refs stable for keyboard handler
  const resultsRef = useRef([]);
  resultsRef.current = [
    ...filteredPages.map((p) => p.to),
    ...filteredClients.map((c) => `/clients/${c.id}`),
  ];
  const selectedIndexRef = useRef(0);
  selectedIndexRef.current = selectedIndex;

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => {
          const next = Math.min(i + 1, resultsRef.current.length - 1);
          itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => {
          const next = Math.max(i - 1, 0);
          itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const to = resultsRef.current[selectedIndexRef.current];
        if (to) go(to);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, go]);

  if (!mounted) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onTransitionEnd={() => { if (!visible) setMounted(false); }}
      />

      {/* Panel */}
      <div
        className={`relative w-full max-w-xl mx-4 bg-white dark:bg-surface-dark rounded-2xl shadow-2xl overflow-hidden border border-border-light dark:border-border transition-all duration-200 ${visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-light dark:border-border">
          <MagnifyingGlassIcon className="w-5 h-5 text-text-muted-light dark:text-text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, clients…"
            className="flex-1 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted outline-none text-base"
          />
          <kbd className="text-xs text-text-muted-light dark:text-text-muted bg-background-light dark:bg-background border border-border-light dark:border-border rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {/* Pages section */}
          {filteredPages.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-text-muted-light dark:text-text-muted uppercase tracking-wider">
                Pages
              </div>
              {filteredPages.map(({ label, to, icon: Icon }, idx) => {
                const i = idx;
                const isSelected = selectedIndex === i;
                return (
                  <button
                    key={to}
                    ref={(el) => (itemRefs.current[i] = el)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    onClick={() => go(to)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${isSelected ? 'bg-black/5 dark:bg-white/5' : ''}`}
                  >
                    <Icon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary flex-shrink-0" />
                    <span className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Clients section */}
          {filteredClients.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-text-muted-light dark:text-text-muted uppercase tracking-wider">
                Clients
              </div>
              {filteredClients.map((c, idx) => {
                const i = clientOffset + idx;
                const isSelected = selectedIndex === i;
                return (
                  <div
                    key={c.id}
                    ref={(el) => (itemRefs.current[i] = el)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    onClick={() => go(`/clients/${c.id}`)}
                    className={`flex items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer ${isSelected ? 'bg-black/5 dark:bg-white/5' : ''}`}
                  >
                    <UserCircleIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                        {c.name}
                      </span>
                      {c.industry && (
                        <span className="ml-2 text-xs text-text-muted-light dark:text-text-muted">
                          {c.industry}
                        </span>
                      )}
                    </div>
                    {/* Quick-nav icon buttons — visible when row is selected */}
                    <div className={`flex items-center gap-0.5 transition-opacity flex-shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`}>
                      {hasRouteAccess('/insights') && (
                        <button
                          title="Insights"
                          onClick={(e) => { e.stopPropagation(); go(`/insights?view=company&id=${c.id}`); }}
                          className="p-1.5 rounded-lg hover:opacity-75 text-text-secondary-light dark:text-text-secondary transition"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasRouteAccess('/clients') && (
                        <button
                          title="Client Workspace"
                          onClick={(e) => { e.stopPropagation(); go(`/clients/${c.id}`); }}
                          className="p-1.5 rounded-lg hover:opacity-75 text-text-secondary-light dark:text-text-secondary transition"
                        >
                          <UserCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                      {hasCMSAccess() && (
                        <button
                          title="CMS"
                          onClick={(e) => { e.stopPropagation(); go(`/cms/companies/${c.id}`); }}
                          className="p-1.5 rounded-lg hover:opacity-75 text-text-secondary-light dark:text-text-secondary transition"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {totalResults === 0 && (
            <div className="px-4 py-10 text-center text-sm text-text-muted-light dark:text-text-muted">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}

          <div className="h-2" />
        </div>
      </div>
    </div>
  );
}
